import {
  normalizeAssistantPresentation,
} from '@triedge/ai-assistant-ui/utils/assistantPresentation';
import type { AssistantPresentationModel } from '@triedge/ai-assistant-ui/types';
import type {
  AdmissionsAIContextualAction,
  AdmissionsAssistantArtifactPreview,
  AdmissionsAssistantPresentationDiagnostics,
  AdmissionsAssistantTaskProposalCard,
  AdmissionsAssistantTaskThreadRollup,
  AdmissionsAssistantTaskThreadStatus,
  AdmissionsCitation,
} from '../components/ai/types';
import {
  composeAssistantPresentation,
  type AssistantPresentationFactBundle,
} from './assistantPresentationComposer';
import {
  extractStructuredAssistantPayload,
  sanitizeAssistantAnswerForDisplay,
} from './assistantPresentationDiscipline';

export type UnifiedAssistantModule =
  | 'admissions'
  | 'financial-aid'
  | 'faculty'
  | 'settings'
  | 'students-families'
  | 'parent-portal'
  | 'general'
  | string;

export type UnifiedAssistantResponseEnvelope = {
  answer: string;
  citations: AdmissionsCitation[];
  actions: AdmissionsAIContextualAction[];
  module: UnifiedAssistantModule;
  debug?: Record<string, unknown> | null;
  presentation?: AssistantPresentationModel | null;
  facts?: AssistantPresentationFactBundle | null;
  artifactPreview?: AdmissionsAssistantArtifactPreview | null;
  taskProposal?: AdmissionsAssistantTaskProposalCard | null;
  taskThread?: AdmissionsAssistantTaskThreadStatus | null;
  taskThreadRollup?: AdmissionsAssistantTaskThreadRollup | null;
  readOnlyDegradedReason?: string | null;
};

export type UnifiedAssistantResponseEnvelopeResult = {
  answer: string;
  citations: AdmissionsCitation[];
  actions: AdmissionsAIContextualAction[];
  module: UnifiedAssistantModule;
  debug: Record<string, unknown> | null;
  presentation: AssistantPresentationModel;
  presentationDiagnostics: AdmissionsAssistantPresentationDiagnostics | null;
};

export type UnifiedAssistantPublicResponseEnvelopeResult = Omit<
  UnifiedAssistantResponseEnvelopeResult,
  'presentationDiagnostics'
>;

const isRecord = (value: unknown): value is Record<string, unknown> => (
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)
);

const hasStructuredObjects = (value: unknown[] | undefined): boolean => (
  Array.isArray(value) && value.some((entry) => entry && typeof entry === 'object' && !Array.isArray(entry))
);

const ASSISTANT_ACTION_TOP_LEVEL_KEYS = new Set([
  'id',
  'label',
  'type',
  'kind',
  'capabilityId',
  'targetRoute',
  'entityRef',
  'requiresConfirmation',
  'approvalCategory',
  'actionCategory',
  'mutationOperation',
  'missingInputs',
  'confidenceMode',
  'payload',
]);

const ASSISTANT_ACTION_TYPES = new Set(['followup', 'navigate', 'execute', 'draft']);
const ASSISTANT_CONFIDENCE_MODES = new Set(['full', 'navigation_only']);

const isOptionalString = (value: unknown): boolean => value === undefined || typeof value === 'string';

const isOptionalNullableString = (value: unknown): boolean => (
  value === undefined || value === null || typeof value === 'string'
);

const isStringArray = (value: unknown): boolean => (
  Array.isArray(value) && value.every((entry) => typeof entry === 'string')
);

const isAssistantEntityRef = (value: unknown): boolean => {
  if (value === undefined || value === null) {
    return true;
  }
  if (!isRecord(value)) {
    return false;
  }
  return (
    typeof value.kind === 'string' &&
    typeof value.id === 'string' &&
    isOptionalNullableString(value.label) &&
    isOptionalString(value.module)
  );
};

const isAssistantContextualAction = (value: unknown): value is AdmissionsAIContextualAction => {
  if (!isRecord(value)) {
    return false;
  }
  if (Object.keys(value).some((key) => !ASSISTANT_ACTION_TOP_LEVEL_KEYS.has(key))) {
    return false;
  }
  const actionType = value.type;
  const actionKind = value.kind;
  const confidenceMode = value.confidenceMode;
  return (
    typeof value.id === 'string' &&
    value.id.trim().length > 0 &&
    typeof value.label === 'string' &&
    value.label.trim().length > 0 &&
    typeof actionType === 'string' &&
    ASSISTANT_ACTION_TYPES.has(actionType) &&
    (actionKind === undefined || (typeof actionKind === 'string' && ASSISTANT_ACTION_TYPES.has(actionKind))) &&
    isOptionalString(value.capabilityId) &&
    isOptionalString(value.targetRoute) &&
    isAssistantEntityRef(value.entityRef) &&
    (value.requiresConfirmation === undefined || typeof value.requiresConfirmation === 'boolean') &&
    isOptionalNullableString(value.approvalCategory) &&
    isOptionalNullableString(value.actionCategory) &&
    isOptionalNullableString(value.mutationOperation) &&
    (value.missingInputs === undefined || isStringArray(value.missingInputs)) &&
    (confidenceMode === undefined || (typeof confidenceMode === 'string' && ASSISTANT_CONFIDENCE_MODES.has(confidenceMode))) &&
    isRecord(value.payload)
  );
};

const coerceExtractedActions = (value: unknown[] | undefined): AdmissionsAIContextualAction[] | null => {
  if (!Array.isArray(value) || !hasStructuredObjects(value)) {
    return null;
  }
  const actions = value.filter(isAssistantContextualAction);
  return actions.length === value.length ? actions : null;
};

const normalizeModule = (value: unknown): UnifiedAssistantModule => {
  const normalized = String(value ?? '').trim().toLowerCase();
  if (!normalized) {
    return 'general';
  }
  if (normalized === 'financialaid' || normalized === 'financial_aid') {
    return 'financial-aid';
  }
  if (normalized === 'parent' || normalized === 'parent_portal') {
    return 'parent-portal';
  }
  return normalized;
};

const coercePresentationDiagnosticsFromDebug = (
  debug: Record<string, unknown> | null | undefined,
): AdmissionsAssistantPresentationDiagnostics | null => {
  const candidate = debug?.presentationDiagnostics;
  if (!isRecord(candidate)) {
    return null;
  }
  const reasonCode = candidate.reasonCode;
  if (reasonCode !== 'machine_payload_sanitized' && reasonCode !== 'machine_payload_blocked') {
    return null;
  }
  const title = typeof candidate.title === 'string' ? candidate.title.trim() : '';
  const summary = typeof candidate.summary === 'string' ? candidate.summary.trim() : '';
  if (!title || !summary) {
    return null;
  }
  return {
    reasonCode,
    title,
    summary,
    operatorGuidance: typeof candidate.operatorGuidance === 'string' ? candidate.operatorGuidance.trim() || null : null,
    module: typeof candidate.module === 'string' ? candidate.module.trim() || null : null,
    targetRoute: typeof candidate.targetRoute === 'string' ? candidate.targetRoute.trim() || null : null,
    displayMode: candidate.displayMode === 'icon' ? 'icon' : candidate.displayMode === 'drawer' ? 'drawer' : undefined,
  };
};

const compactText = (value: unknown): string => String(value ?? '').replace(/\s+/g, ' ').trim();

const readRecordArray = (value: unknown): Record<string, unknown>[] => (
  Array.isArray(value)
    ? value.filter((entry): entry is Record<string, unknown> =>
      Boolean(entry) && typeof entry === 'object' && !Array.isArray(entry))
    : []
);

const readNumber = (value: unknown): number => (
  typeof value === 'number' && Number.isFinite(value) ? value : 0
);

const normalizePipelineStageLabel = (value: unknown): string => (
  compactText(value)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (token) => token.toUpperCase())
);

const needsAdmissionsPipelinePresentationRepair = (
  debug: Record<string, unknown>,
  presentation: AssistantPresentationModel | null,
): boolean => {
  if (compactText(debug.responseDecision) !== 'deterministic_pipeline_bottlenecks') {
    return false;
  }
  if (!presentation) {
    return true;
  }
  const sectionTypes = new Set(presentation.sections.map((section) => section.type));
  return (
    presentation.responseKind !== 'multi_artifact_insight'
    || !sectionTypes.has('chart')
    || !sectionTypes.has('table')
    || !sectionTypes.has('artifacts')
  );
};

const buildAdmissionsPipelineSummaryFromDebug = (
  debug: Record<string, unknown>,
): string | null => {
  if (compactText(debug.responseDecision) !== 'deterministic_pipeline_bottlenecks') {
    return null;
  }
  const aggregate = isRecord(debug.aggregateInsights) ? debug.aggregateInsights : null;
  const stageDistribution = readRecordArray(aggregate?.stageDistribution);
  if (!aggregate || stageDistribution.length === 0) {
    return null;
  }
  const bottleneckStages = readRecordArray(aggregate.bottleneckStages);
  const pipelineTotals = isRecord(aggregate.pipelineTotals) ? aggregate.pipelineTotals : null;
  const applicationCount = readNumber(pipelineTotals?.applications)
    || stageDistribution.reduce((sum, entry) => sum + readNumber(entry.count), 0);
  const largestStage = stageDistribution[0];
  const highestRiskBottleneck = bottleneckStages[0] ?? null;
  return compactText([
    'Admissions pipeline bottleneck analysis.',
    `Coverage: Full admissions dataset (${applicationCount} applications). [Source 1]`,
    `Largest stage: ${normalizePipelineStageLabel(largestStage.status)} (${readNumber(largestStage.count)}).`,
    highestRiskBottleneck
      ? `Highest-risk bottleneck: ${normalizePipelineStageLabel(highestRiskBottleneck.status)} with ${readNumber(highestRiskBottleneck.count)} applicants and ${readNumber(highestRiskBottleneck.avgDwellDays)} average dwell days.`
      : 'No stage currently exceeds the configured bottleneck thresholds.',
    'Conversion rates are unavailable because current-stage records are not cohort transition history, so I am not reporting conversion percentages.',
    'Use the Chart, Table, and Export tabs for the supporting evidence.',
  ].join(' '));
};

const buildAdmissionsPipelineSummaryFromAnswer = (answer: string): string | null => {
  const normalized = compactText(answer);
  if (!/Admissions pipeline bottleneck analysis/i.test(normalized) || !/Bottleneck stages:/i.test(normalized)) {
    return null;
  }
  const applicationCount = Number(normalized.match(/Full admissions dataset \((\d+) applications\)/i)?.[1] ?? 0);
  const largestMatch = normalized.match(/Largest stages:\s*([^:.;]+):?\s*(\d+)\s+applicants/i)
    ?? normalized.match(/Largest stage:\s*([^(.;]+)\((\d+)\)/i);
  const bottleneckMatch = normalized.match(/Bottleneck stages:\s*([^:.;]+):\s*(\d+)\s+applicants;\s*avg dwell\s*([\d.]+)\s*days/i)
    ?? normalized.match(/Highest-risk bottleneck:\s*([^.;]+?)\s+with\s+(\d+)\s+applicants\s+and\s+([\d.]+)\s+average dwell days/i);
  const largestStageLabel = normalizePipelineStageLabel(largestMatch?.[1]);
  const largestStageCount = Number(largestMatch?.[2] ?? 0);
  const bottleneckLabel = normalizePipelineStageLabel(bottleneckMatch?.[1]);
  const bottleneckCount = Number(bottleneckMatch?.[2] ?? 0);
  const bottleneckDwell = Number(bottleneckMatch?.[3] ?? 0);
  if (!applicationCount && !largestStageLabel && !bottleneckLabel) {
    return null;
  }
  return compactText([
    'Admissions pipeline bottleneck analysis.',
    applicationCount > 0
      ? `Coverage: Full admissions dataset (${applicationCount} applications). [Source 1]`
      : null,
    largestStageLabel
      ? `Largest stage: ${largestStageLabel}${largestStageCount > 0 ? ` (${largestStageCount})` : ''}.`
      : null,
    bottleneckLabel
      ? `Highest-risk bottleneck: ${bottleneckLabel}${bottleneckCount > 0 ? ` with ${bottleneckCount} applicants` : ''}${bottleneckDwell > 0 ? ` and ${bottleneckDwell} average dwell days` : ''}.`
      : 'No stage currently exceeds the configured bottleneck thresholds.',
    'Conversion rates are unavailable because current-stage records are not cohort transition history, so I am not reporting conversion percentages.',
    'Use the Chart, Table, and Export tabs for the supporting evidence.',
  ].filter(Boolean).join(' '));
};

const repairAdmissionsPipelineAnswer = (
  answer: string,
  debug: Record<string, unknown>,
): string => {
  const normalized = compactText(answer);
  const deterministicSummary = buildAdmissionsPipelineSummaryFromDebug(debug);
  if (deterministicSummary) {
    return deterministicSummary;
  }
  const proseSummary = buildAdmissionsPipelineSummaryFromAnswer(normalized);
  if (proseSummary) {
    return proseSummary;
  }
  if (!/\bAdmissions Pipeline Bottlenecks\b/i.test(normalized)) {
    return normalized;
  }
  return normalized.replace(/^Admissions Pipeline Bottlenecks\./i, 'Admissions pipeline bottleneck analysis.');
};

const repairAdmissionsPipelinePresentationSummary = (
  presentation: AssistantPresentationModel,
  answer: string,
  debug: Record<string, unknown>,
): AssistantPresentationModel => {
  void debug;
  if (!/Admissions pipeline bottleneck analysis/i.test(answer)) {
    return presentation;
  }
  let repaired = false;
  const sections = presentation.sections.map((section) => {
    if (section.type !== 'summary' || repaired) {
      return section;
    }
    repaired = true;
    return {
      ...section,
      text: answer,
    };
  });
  return repaired
    ? {
        ...presentation,
        sections,
      }
    : presentation;
};

export const repairAssistantPresentationForDisplay = (
  presentation: AssistantPresentationModel,
  answer: string,
): AssistantPresentationModel => (
  repairAdmissionsPipelinePresentationSummary(
    presentation,
    repairAdmissionsPipelineAnswer(answer, {}),
    {},
  )
);

export const repairAssistantAnswerForDisplayText = (answer: string): string =>
  repairAdmissionsPipelineAnswer(answer, {});

export const normalizeUnifiedAssistantResponseEnvelope = (
  input: UnifiedAssistantResponseEnvelope & { pathname?: string | null },
): UnifiedAssistantResponseEnvelopeResult => {
  const module = normalizeModule(input.module);
  const extracted = extractStructuredAssistantPayload(input.answer);
  const citations = hasStructuredObjects(extracted?.citations)
    ? extracted!.citations as AdmissionsCitation[]
    : input.citations;
  const extractedActions = coerceExtractedActions(extracted?.actions);
  const actions = extractedActions ?? input.actions;
  const sanitized = sanitizeAssistantAnswerForDisplay({
    answer: extracted?.answer ?? input.answer,
    module,
    originalAnswer: input.answer,
  });
  const debug = {
    ...(isRecord(extracted?.debug) ? extracted!.debug : {}),
    ...(isRecord(input.debug) ? input.debug : {}),
    ...(sanitized.diagnostics
      ? {
          presentationDiagnostics: sanitized.diagnostics,
          presentationContract: {
            sanitized: true,
          },
        }
      : {}),
  } satisfies Record<string, unknown>;
  const presentationDiagnostics = sanitized.diagnostics ?? coercePresentationDiagnosticsFromDebug(debug);

  const explicitPresentation = normalizeAssistantPresentation(input.presentation);
  const repairExplicitPresentation = needsAdmissionsPipelinePresentationRepair(debug, explicitPresentation);
  const normalizedAnswer = repairAdmissionsPipelineAnswer(sanitized.answer, debug);
  const displayExplicitPresentation = explicitPresentation
    ? repairAdmissionsPipelinePresentationSummary(explicitPresentation, normalizedAnswer, debug)
    : null;
  if (displayExplicitPresentation && !repairExplicitPresentation) {
    return {
      answer: normalizedAnswer,
      citations,
      actions,
      module,
      debug: {
        ...debug,
        presentationContract: {
          ...(isRecord(debug.presentationContract) ? debug.presentationContract : {}),
          clientProjectionUsed: false,
          serverSectionTypes: displayExplicitPresentation.sections.map((section) => section.type),
          visibleSectionTypes: displayExplicitPresentation.sections
            .map((section) => section.type)
            .filter((type) => !['sources', 'related_links', 'diagnostics'].includes(type)),
          supportSectionTypes: displayExplicitPresentation.sections
            .map((section) => section.type)
            .filter((type) => ['sources', 'related_links', 'diagnostics'].includes(type)),
          responseKind: displayExplicitPresentation.responseKind ?? null,
        },
      },
      presentation: displayExplicitPresentation,
      presentationDiagnostics,
    };
  }

  const composition = composeAssistantPresentation({
    answer: normalizedAnswer,
    citations,
    actions,
    module,
    pathname: input.pathname,
    debug,
    facts: input.facts,
    artifactPreview: input.artifactPreview ?? null,
    taskProposal: input.taskProposal ?? null,
    taskThread: input.taskThread ?? null,
    taskThreadRollup: input.taskThreadRollup ?? null,
    diagnostics: presentationDiagnostics,
    readOnlyDegradedReason: input.readOnlyDegradedReason ?? null,
    clientProjectionUsed: repairExplicitPresentation,
  });
  const nextDebug = {
    ...debug,
    presentationContract: {
      ...(isRecord(debug.presentationContract) ? debug.presentationContract : {}),
      ...composition.contractDiagnostics,
      responseKind: composition.responseKind,
      clientProjectionUsed: composition.clientProjectionUsed,
    },
  };

  return {
    answer: normalizedAnswer,
    citations,
    actions,
    module,
    debug: nextDebug,
    presentation: composition.presentation,
    presentationDiagnostics,
  };
};

export const normalizeUnifiedAssistantPublicResponseEnvelope = (
  input: UnifiedAssistantResponseEnvelope & { pathname?: string | null },
): UnifiedAssistantPublicResponseEnvelopeResult => {
  const normalized = normalizeUnifiedAssistantResponseEnvelope(input);
  return {
    answer: normalized.answer,
    citations: normalized.citations,
    actions: normalized.actions,
    module: normalized.module,
    debug: normalized.debug,
    presentation: normalized.presentation,
  };
};
